/**
 * Typsicherer API-Client.
 *
 * - credentials: 'include' → schickt Cockpit-Session-Cookie mit
 * - 401-Response wirft Error mit `not_authenticated`-Marker. React-Query
 *   verarbeitet das als Error; App.tsx zeigt die Login-Hinweis-UI.
 *   KEIN harter window.location.href = '/' mehr (sonst Endlos-Redirect-Schleife
 *   wenn die SPA in einem Tab ohne Cockpit-Session geöffnet wird).
 */

import type {
  DimensionsResponse,
  EtlStatus,
  InsightsResponse,
  FilterRequest,
  GeoRequest,
  GeoResponse,
  HeatmapResponse,
  KpiResponse,
  PivotRequest,
  PivotResponse,
  StornoRequest,
  StornoResponse,
  TimeseriesRequest,
  TimeseriesResponse,
  TopNRequest,
  TopNResponse,
} from '@statistik/shared-types';

export interface MeResponse {
  id: number;
  username: string;
  display_name: string;
  role: string;
  accessible_store_count: number;
  total_active_stores: number;
  has_full_access: boolean;
  stores: Array<{ id: number; store_number: string; name: string }>;
}

export interface CompareRequest {
  filters: Record<string, unknown>;
  periode_a: { min: string; max: string };
  periode_b: { min: string; max: string };
  groupBy: 'store_name' | 'warengruppe_name' | 'plz' | 'monat' | 'wochentag';
  limit?: number;
}
export interface CompareRow {
  label: string;
  umsatz_a: number;
  umsatz_b: number;
  diff_abs: number;
  diff_pct: number | null;
  anz_bons_a: number;
  anz_bons_b: number;
}
export interface CompareResponse {
  rows: CompareRow[];
  total: {
    umsatz_a: number;
    umsatz_b: number;
    diff_abs: number;
    diff_pct: number | null;
  };
  periode_a: { min: string; max: string };
  periode_b: { min: string; max: string };
  groupBy: string;
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    throw new Error('not_authenticated');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

const BASE = '/api/stat/v1';

export const api = {
  me: () => fetchJson<MeResponse>(`${BASE}/me`),
  dimensions: () => fetchJson<DimensionsResponse>(`${BASE}/dimensions`),
  kpis: (req: FilterRequest) =>
    fetchJson<KpiResponse>(`${BASE}/kpis`, { method: 'POST', body: JSON.stringify(req) }),
  timeseries: (req: TimeseriesRequest) =>
    fetchJson<TimeseriesResponse>(`${BASE}/timeseries`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),
  topn: (req: TopNRequest) =>
    fetchJson<TopNResponse>(`${BASE}/topn`, { method: 'POST', body: JSON.stringify(req) }),
  heatmap: (req: FilterRequest) =>
    fetchJson<HeatmapResponse>(`${BASE}/heatmap`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),
  pivot: (req: PivotRequest) =>
    fetchJson<PivotResponse>(`${BASE}/pivot`, { method: 'POST', body: JSON.stringify(req) }),
  geo: (req: GeoRequest) =>
    fetchJson<GeoResponse>(`${BASE}/geo`, { method: 'POST', body: JSON.stringify(req) }),
  stornos: (req: StornoRequest) =>
    fetchJson<StornoResponse>(`${BASE}/stornos`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),
  etlStatus: () => fetchJson<EtlStatus>(`${BASE}/admin/etl/status`),
  compare: (req: CompareRequest) =>
    fetchJson<CompareResponse>(`${BASE}/compare`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  // Favoriten
  favoritesList: () =>
    fetchJson<FavoriteDTO[]>(`${BASE}/favorites`),
  favoritesCreate: (body: { name: string; filters: Record<string, unknown>; path?: string }) =>
    fetchJson<FavoriteDTO>(`${BASE}/favorites`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  favoritesUpdate: (id: number, patch: Partial<{ name: string; filters: Record<string, unknown>; pinned: boolean }>) =>
    fetchJson<{ ok: true }>(`${BASE}/favorites/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  favoritesDelete: (id: number) =>
    fetchJson<{ ok: true }>(`${BASE}/favorites/${id}`, { method: 'DELETE' }),

  benchmark: (req: BenchmarkRequest) =>
    fetchJson<BenchmarkResponse>(`${BASE}/benchmark`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),
  marktaktivitaet: () => fetchJson<AktivitaetResponse>(`${BASE}/marktaktivitaet`),
  wgTagesumsatz: (req: WgTagesumsatzRequest) =>
    fetchJson<WgTagesumsatzResponse>(`${BASE}/wg-tagesumsatz`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),
  dimensionsWithExclusions: (filters: Record<string, unknown>) =>
    fetchJson<DimensionsWithExclusions>(`${BASE}/dimensions`, {
      method: 'POST',
      body: JSON.stringify({ filters }),
    }),

  // KI-Analyse: Prognose + Anomalien + Insight-Karten
  insights: (req: { filters: Record<string, unknown>; horizon?: number }) =>
    fetchJson<InsightsResponse>(`${BASE}/insights`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  // Regionen = Cockpit-Gruppen mit ihren Filialen (für Filial-Auswahl nach Region)
  regionen: () => fetchJson<{ regionen: Region[] }>(`${BASE}/regionen`),

  // Artikel-Bericht: Suche (Bezeichnung/EAN/Mono-Nr/DRS-Nr) + Auswertung
  artikelSearch: (q: string) =>
    fetchJson<{ results: ArtikelHit[] }>(`${BASE}/artikel/search?q=${encodeURIComponent(q)}`),
  artikelReport: (eans: string[], filters: Record<string, unknown>) =>
    fetchJson<ArtikelReportResponse>(`${BASE}/artikel/report`, {
      method: 'POST',
      body: JSON.stringify({ eans, filters }),
    }),
  artikelStoreDetail: (eans: string[], store_name: string, filters: Record<string, unknown>) =>
    fetchJson<ArtikelStoreDetail>(`${BASE}/artikel/store-detail`, {
      method: 'POST',
      body: JSON.stringify({ eans, store_name, filters }),
    }),
};

export interface Region {
  id: number;
  name: string;
  stores: Array<{ id: number; store_number: string; store_name: string }>;
}
export interface ArtikelHit {
  ean: string;
  name: string | null;
  artikel_nr: string | null; // Mono-Artikelnr
  artikel_id: string | null; // DRS-Artikelnr
  umsatz: number;
  menge: number;
}
export interface ArtikelReportResponse {
  totals: { umsatz: number; menge: number; bons: number };
  series: Array<{ zeitpunkt: string; umsatz: number; menge: number }>;
  stores: Array<{ store_name: string; umsatz: number; menge: number }>;
  articles: Array<{ ean: string; name: string | null; umsatz: number; menge: number }>;
}
export interface ArtikelStoreDetail {
  store_name: string;
  totals: { umsatz: number; menge: number; bons: number; tage: number };
  monthly: Array<{ zeitpunkt: string; umsatz: number; menge: number }>;
  daily: Array<{ tag: string; umsatz: number; menge: number }>;
  articles: Array<{ ean: string; name: string | null; umsatz: number; menge: number }>;
}

export interface BenchmarkRequest {
  store_id: number;
  periode: { min: string; max: string };
}
export interface BenchmarkResponse {
  rows: Array<{
    warengruppe_name: string;
    umsatz_eigen: number;
    umsatz_schnitt: number;
    diff_abs: number;
    diff_pct: number | null;
  }>;
  total_eigen: number;
  total_schnitt: number;
  total_diff_pct: number | null;
  anzahl_vergleichsfilialen: number;
}
export interface AktivitaetResponse {
  rows: Array<{
    store_id: number;
    store_number: string;
    store_name: string;
    ort: string;
    tage_mit_daten: number;
    letzter_tag: string | null;
    gesamtumsatz: number;
    gesamtbons: number;
    anteil_tage_pct: number;
  }>;
  total_stores: number;
}
export interface WgTagesumsatzRequest {
  store_id: number;
  periode: { min: string; max: string };
}
export interface WgTagesumsatzWgValue {
  brutto: number;
  anteil_pct: number;
}
export interface WgTagesumsatzSummaryValue {
  brutto: number;
  netto: number;
  mwst_7: number;
  mwst_19: number;
}
export interface WgTagesumsatzResponse {
  store: { id: number };
  periode: { min: string; max: string };
  warengruppen: Array<{ id: string; name: string; umsatz: number }>;
  days: Array<{
    datum: string;
    wochentag: number;
    kw: number;
    wg_values: Record<string, WgTagesumsatzWgValue>;
    tag_total: number;
    anz_kunden: number;
    umsatz_pro_kunde: number;
  }>;
  weeks: Array<{
    kw: number;
    jahr: number;
    week_start: string;
    week_end: string;
    wg_values: Record<string, WgTagesumsatzWgValue>;
    week_total: number;
    week_netto: number;
    anz_kunden: number;
    umsatz_pro_kunde: number;
  }>;
  summary: {
    wg_values: Record<string, WgTagesumsatzSummaryValue>;
    total: WgTagesumsatzSummaryValue;
    anz_kunden: number;
    umsatz_pro_kunde: number;
  };
}

export interface DimensionsWithExclusions {
  dimensions: Array<{
    dimension: string;
    values: Array<{
      value: string | number;
      label: string;
      count: number;
      available: boolean;
    }>;
  }>;
}

export interface FavoriteDTO {
  id: number;
  user_id: number;
  name: string;
  filters: Record<string, unknown>;
  path: string;
  pinned: boolean;
  created_at: string;
  updated_at: string;
}
